/**
 * Join a Class – Student page listing active sessions for their school.
 * Students can join directly without the teacher sharing the code.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClassSession } from '../../contexts/ClassSessionContext';
import { useLiveClassSessions } from '../../hooks/useLiveClassSessions';
import { Card, CardContent } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { FaChalkboardTeacher, FaUsers, FaVideo, FaArrowRight } from 'react-icons/fa';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { toast } from 'react-toastify';

const JoinClassPage = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { joinSession, sessionLoading, joinedSessionId, isWaitingForApproval } = useClassSession();
  // Scoped to the student's own classes — this page used to list every session in
  // the school, so students could see and join other classes' lessons.
  const { sessions, loading } = useLiveClassSessions('my-classes');
  const [joiningCode, setJoiningCode] = useState<string | null>(null);
  const [sessionCode, setSessionCode] = useState('');

  const schoolId = profile?.school_id || (profile as { managed_school_id?: string })?.managed_school_id;
  const isStudent = profile?.role === 'student';

  const handleJoin = async (sessionCode: string) => {
    setJoiningCode(sessionCode);
    try {
      const ok = await joinSession(sessionCode);
      if (ok) {
        toast.success('Joined class. Go to your dashboard to wait for the lesson.');
        navigate('/dashboard/student', { replace: true });
      }
    } finally {
      setJoiningCode(null);
    }
  };

  const handleCodeJoin = async () => {
    const code = sessionCode.trim().toUpperCase();
    if (!code) return;
    await handleJoin(code);
  };

  if (isWaitingForApproval) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-24">
        <Card className="max-w-md w-full rounded-xl border border-border bg-card shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
                <FaChalkboardTeacher className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-foreground font-medium mb-1">Waiting for teacher approval</p>
            <p className="text-sm text-muted-foreground mb-6">
              You were previously removed from this class. The teacher has been notified of your request to rejoin and must approve it before you can enter.
            </p>
            <Button asChild variant="outline">
              <Link to="/dashboard/student">Go back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-24">
        <Card className="max-w-md w-full rounded-xl border border-border bg-card shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
                <FaVideo className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-foreground font-medium mb-1">Sign in to join a class</p>
            <p className="text-sm text-muted-foreground mb-6">View active classes from your school and join with one click.</p>
            <Button asChild>
              <Link to="/login">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isStudent) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="rounded-xl border border-border bg-card shadow-sm">
            <CardContent className="p-8 text-center">
              <FaChalkboardTeacher className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium">Only students can join a class from this page.</p>
              <p className="text-sm text-muted-foreground mt-1">Teachers and admins manage classes from their dashboards.</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/">Go home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!schoolId) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={learnXRFontStyle}>
              <FaVideo className="text-primary" />
              Join a class
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Enter the code shared by your Channel Partner to join a live demo.
            </p>
          </header>
          <Card className="rounded-xl border border-border bg-card shadow-sm">
            <CardContent className="p-8 text-center">
              <FaUsers className="h-12 w-12 text-muted-foreground/80 mx-auto mb-4" />
              <p className="text-foreground font-medium">Join with a class code</p>
              <p className="text-sm text-muted-foreground mt-2">Guest access is available only for active Channel Partner demo sessions.</p>
              <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row">
                <input
                  value={sessionCode}
                  onChange={(event) => setSessionCode(event.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={8}
                  className="h-10 flex-1 rounded-md border border-border bg-background px-3 font-mono uppercase tracking-widest"
                  aria-label="Class session code"
                />
                <Button onClick={handleCodeJoin} disabled={!sessionCode.trim() || sessionLoading}>
                  {sessionLoading ? 'Joining…' : 'Join class'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={learnXRFontStyle}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
              <FaVideo className="h-5 w-5 text-primary" />
            </div>
            Join a class
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Active classes from your school. Click Join to enter — no code needed.
          </p>
        </header>

        {loading ? (
          <Card className="rounded-xl border border-border bg-card shadow-sm">
            <CardContent className="py-16 text-center">
              <p className="text-muted-foreground">Loading active classes…</p>
            </CardContent>
          </Card>
        ) : sessions.length === 0 ? (
          <Card className="rounded-xl border border-border bg-card shadow-sm">
            <CardContent className="p-8 sm:p-10 text-center">
              <div className="flex justify-center mb-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted/50 border border-border">
                  <FaChalkboardTeacher className="h-7 w-7 text-muted-foreground" />
                </div>
              </div>
              <p className="text-foreground font-semibold text-lg">No active classes right now</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                When a teacher starts a class session, it will appear here. You can also join with a session code from your dashboard.
              </p>
              <Button asChild variant="outline" className="mt-6">
                <Link to="/dashboard/student">My dashboard</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const isJoined = joinedSessionId === session.id;
              const isJoining = sessionLoading && joiningCode === session.session_code;
              return (
                <Card key={session.id} className="rounded-xl border border-border bg-card shadow-sm hover:border-primary/30 hover:shadow-md transition-all">
                  <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground truncate">
                        {session.className || session.class_id}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                        <FaChalkboardTeacher className="w-3.5 h-3.5 shrink-0" />
                        {session.teacherName}
                      </p>
                      {session.launched_lesson && (
                        <p className="text-xs text-primary mt-1.5 font-medium">
                          Lesson: {session.launched_lesson.chapter_id} → {session.launched_lesson.topic_id}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground font-mono hidden sm:inline bg-muted/50 px-2 py-1 rounded">
                        {session.session_code}
                      </span>
                      {isJoined ? (
                        <Button size="default" variant="secondary" asChild className="gap-1.5">
                          <Link to="/dashboard/student">
                            In class
                            <FaArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </Button>
                      ) : (
                        <Button
                          size="default"
                          onClick={() => handleJoin(session.session_code)}
                          disabled={isJoining}
                        >
                          {isJoining ? 'Joining…' : 'Join'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-center mt-8">
          <Link to="/dashboard/student" className="text-sm text-primary font-medium hover:underline inline-flex items-center gap-1">
            ← Back to dashboard
          </Link>
        </p>
      </div>
    </div>
  );
};

export default JoinClassPage;
